import React, { useState } from "react";
import "react-phone-number-input/style.css";
import PhoneInput, { Country } from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import clsx from "clsx";
import { getPhonePlaceholder } from "../../../utils/miscFunctions";
import { id } from "date-fns/locale";
import { FormikErrors } from "formik";
import { ScrollToFormikError } from "../../elements/ScrollToFormikError";
import { motion } from "framer-motion";
import { Search } from "lucide-react";

interface Props {
  onChange: (value?: string | undefined) => void;
  value: string | undefined;
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  onBlur?: (e: React.FocusEvent<HTMLElement, Element>) => void;
  placeholder?: string;
  onKeyDown?: (e:React.KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  required?: boolean;
  classes?: string;
  primaryColor?: string;
  secondaryColor?: string;
  country?: string;
  onCountryChange?: (country: CountryCode) => void;
  disabled?: boolean
  description?: string;
  helperText?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  scrollToFormik?: boolean;
  showSearchIcon?: boolean;
  SearchIcon?: JSX.Element;
  autoFocus?: boolean;
  labelClasses?: string;
}

type Tagged<A, T> = A & { __tag?: T };
type E164Number = Tagged<string, "E164Number">;

const IntlPhoneInput = ({
  onChange,
  value,
  error,
  onBlur,
  placeholder: customPlaceholder,
  onKeyDown,
  label,
  required = true,
  classes,
  primaryColor = '#fbc02d',
  country = 'GH',
  disabled = false,
  onCountryChange,
  description,
  helperText,
  scrollToFormik = true,
  showSearchIcon = false,
  SearchIcon,
  autoFocus=false,
  labelClasses
}: Props) => {
  const [touched, setTouched] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(country as CountryCode);
  const placeholder = customPlaceholder || getPhonePlaceholder(selectedCountry);

  return (
    <div>
      {label && (
        <label
          className={clsx('block text-sm font-medium mb-1 text-gray-700 dark:text-darkBg-mainText', {
            ['!text-danger-main']: error && required
          }, labelClasses)}>
          {label} <span className="text-danger-main">{required && '*'}</span>
          {description && (
            <p
              className={clsx('text-xs text-gray-400 italic', {
                ['text-danger-border']: error && required
              })}>
              {description}
            </p>
          )}
        </label>
      )}
      <style>
        {`
          .PhoneInput {
            border: 1px solid #d1d5db;
            border-radius: 0.4rem;
          }
          .PhoneInput.PhoneInput--focus {
            border: 1px solid ${primaryColor} !important;
          }
          .PhoneInputInput {
            border: none !important;
            border-radius: 0.4rem;
            padding: 0.4rem;
            height: 35px;
            margin-right: 10px;
            outline: 2px solid transparent;
            color: #1f2937;
            background-color: white;
          }
          
          .PhoneInputInput:focus {
            outline: none !important;
            border: none !important;
            box-shadow: none !important;
          }
          .PhoneInputInput--focus {
            outline: none !important;
          }
          .PhoneInputInput::placeholder {
            font-size: 14px;
            color: #d1d5db;
          }
          .PhoneInputCountry {
            margin-left: 8px;
          }
          
          /* Dark mode styles */
          .dark .PhoneInput {
            border-color: #374151;
            background-color: #1f2937;
          }
          .dark .PhoneInputInput {
            color: #f3f4f6;
            background-color: #1f2937;
          }
          .dark .PhoneInputInput::placeholder {
            color: #6b7280;
          }
          .dark .PhoneInputCountry {
            background-color: #1f2937;
            color: #f3f4f6;
          }
          .dark .PhoneInputCountry select {
            background-color: #1f2937;
            color: #f3f4f6;
            border-color: #374151;
          }
        `}
      </style>
      {showSearchIcon && SearchIcon }
      <PhoneInput
        autoFocus={autoFocus}
        className={clsx("w-full p-0 !rounded-full", classes, { ["animate-wiggle"]: error })}
        defaultCountry={country as CountryCode}
        countries={["GH", 'NG']}
        placeholder={placeholder}
        onBlur={onBlur}
        value={value}
        onChange={(value) => {
          if (!touched) setTouched(true);
          onChange(value);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {onKeyDown?.(e)}}
        onCountryChange={(country: CountryCode) => {
          if (country) {
            setSelectedCountry(country);
            onCountryChange?.(country);
          }
        }}
        disabled={disabled}
        useNationalFormatForDefaultCountry={true}
      />
      {/* {touched && error && (
        <span className="mt-4 text-xs text-danger-main">{error}</span>
      )} */}

      {/* Helper Text */}
      <div className={clsx('text-xs text-gray-400', { ['!text-danger-main']: error })}>
        {helperText && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center font-medium"
          >
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {helperText && typeof helperText === 'string' ? helperText : undefined}
          </motion.p>
        )}
      </div>
      {/* {scrollToFormik && <ScrollToFormikError fieldName={name ?? id ?? ''} fieldRef={fieldRef} />} */}
    </div>
  );
};

export default IntlPhoneInput;
